import prepareComponent from "@bluframe/grapple/prepareComponent"
import useToggle from "@bluframe/grapple/useToggle"
import React, {
  FocusEventHandler,
  type Ref,
  RefObject,
  useMemo,
  useRef,
  useState
} from "react"

import Select from "./Select"

export type OptionValue = number | string

export interface Option {
  label: React.ReactNode
  value: OptionValue
}

export interface Props {
  defaultValue?: string
  className?: string
  label: React.ReactNode
  loading?: boolean
  name: string
  onBlur?: FocusEventHandler

  onChange?: (value: OptionValue | null) => void
  options: Option[]
  placeholder?: string
  ref?: Ref<HTMLInputElement>
  required?: boolean
  value?: OptionValue | null
}

export interface ComponentProps extends Props {
  displayValue: React.ReactNode
  internalValue?: OptionValue
  isControlled: boolean
  isLabelShrunk: boolean
  isOpen: boolean

  onChange: (value: OptionValue | null) => () => void
  onToggleIsOpen: () => void
  wrapperRef: RefObject<HTMLDivElement | null>
}

const DEFAULT_IS_OPEN = false

const usePrepareComponent = (props: Props): ComponentProps => {
  const [isOpen, toggleIsOpen] = useToggle(DEFAULT_IS_OPEN)

  // Internal state for selected value
  const [internalValue, setInternalValue] = useState<OptionValue | undefined>(
    props.defaultValue
  )

  // Determine if the component is controlled
  const isControlled = props.value !== undefined

  const wrapperRef = useRef<HTMLDivElement>(null)

  // Get displayValue and isLabelShrunk
  const [displayValue, isLabelShrunk] = useMemo(() => {
    // Get selected option
    const selectedOption = props.options.find(
      (option) =>
        (!isControlled && option.value === internalValue) ||
        option.value === props.value
    )

    // If selected option exists, return its label and shrink the label
    if (selectedOption) {
      return [selectedOption.label, true]
    }

    // If no selected option and placeholder exists, return placeholder and shrink the label
    if (props.placeholder) {
      return [props.placeholder, true]
    }

    // Otherwise, return the label and shrink it when options are open
    return ["", isOpen]
  }, [
    props.options,
    props.placeholder,
    props.value,
    isOpen,
    isControlled,
    internalValue
  ])

  const onBlur: FocusEventHandler = (event) => {
    if (props.onBlur) {
      props.onBlur(event)
    }
  }

  const onToggleIsOpen = () => {
    // If the dropdown is currently open and is being closed by a click, trigger onBlur
    if (isOpen) {
      if (wrapperRef.current) {
        wrapperRef.current.blur()
      }
    }

    // Toggle options dropdown
    toggleIsOpen()
  }

  const onChange = (value: OptionValue | null) => () => {
    // If uncontrolled set internal value
    if (!isControlled) {
      setInternalValue(value as OptionValue)
    }

    // Select via onSelect from props
    props.onChange?.(value)

    // If open close options dropdown
    if (isOpen) {
      onToggleIsOpen()
    }
  }

  return {
    ...props,
    displayValue,
    internalValue,
    isControlled,
    isLabelShrunk,
    isOpen,
    onBlur,
    onChange,
    onToggleIsOpen,
    wrapperRef
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Select
)
